import { useEffect, useMemo, useState } from 'react';
import type {
  AlertConfigResponse,
  WarnThresholdsConfig,
} from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9 섹션 5 — 반복 WARN 이벤트의 자동 에스컬레이션.
// 예: 최근 60분 내 동일 event_code 의 WARN 이 10회 이상 → ERROR 로 격상.

const ESCALATE_OPTIONS: WarnThresholdsConfig['escalateTo'][] = ['ERROR', 'FATAL'];

function equal(a: WarnThresholdsConfig, b: WarnThresholdsConfig): boolean {
  return (
    a.enabled === b.enabled &&
    a.windowMinutes === b.windowMinutes &&
    a.frequencyThreshold === b.frequencyThreshold &&
    a.escalateTo === b.escalateTo
  );
}

interface Props {
  config: AlertConfigResponse;
  onSave: (next: WarnThresholdsConfig) => void;
  isSaving: boolean;
}

export function WarnThresholdsSection({
  config,
  onSave,
  isSaving,
}: Props): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WarnThresholdsConfig>(config.warnThresholds);

  useEffect(() => {
    if (!editing) setDraft(config.warnThresholds);
  }, [config.warnThresholds, editing]);

  const dirty = useMemo(
    () => !equal(draft, config.warnThresholds),
    [draft, config.warnThresholds]
  );
  // 유효성: window / frequency 는 양수여야 한다.
  const valid = draft.windowMinutes > 0 && draft.frequencyThreshold > 0;

  const startEdit = (): void => {
    setDraft(config.warnThresholds);
    setEditing(true);
  };
  const cancel = (): void => {
    setDraft(config.warnThresholds);
    setEditing(false);
  };
  const save = (): void => {
    if (!valid) return;
    onSave(draft);
    setEditing(false);
  };

  return (
    <section className="as-card" data-testid="as-warn-section">
      <div className="as-section__head">
        <div>
          <h3 className="as-section__title">4. WARN 에스컬레이션</h3>
          <div className="as-section__sub">
            지정 시간 창 내에 같은 WARN 이벤트가 임계값 이상 반복되면 상위
            심각도로 자동 격상합니다.
          </div>
        </div>
        <div className="as-section__actions">
          {dirty && <span className="as-dirty">저장되지 않음</span>}
          {!editing ? (
            <button type="button" className="as-btn" onClick={startEdit}>
              편집
            </button>
          ) : (
            <>
              <button
                type="button"
                className="as-btn as-btn--ghost"
                onClick={cancel}
                disabled={isSaving}
              >
                취소
              </button>
              <button
                type="button"
                className="as-btn as-btn--primary"
                onClick={save}
                disabled={!valid || !dirty || isSaving}
              >
                저장
              </button>
            </>
          )}
        </div>
      </div>

      <div className="as-row">
        <label className="as-chip">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({ ...p, enabled: e.target.checked }))
            }
          />
          <span>활성화</span>
        </label>

        <div className="as-field">
          <label className="as-field__label" htmlFor="as-warn-window">
            시간 창 (분)
          </label>
          <input
            id="as-warn-window"
            type="number"
            min={1}
            className="as-input"
            value={draft.windowMinutes}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                windowMinutes: Number.parseInt(e.target.value || '0', 10),
              }))
            }
          />
        </div>

        <div className="as-field">
          <label className="as-field__label" htmlFor="as-warn-freq">
            임계 빈도 (회)
          </label>
          <input
            id="as-warn-freq"
            type="number"
            min={1}
            className="as-input"
            value={draft.frequencyThreshold}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                frequencyThreshold: Number.parseInt(e.target.value || '0', 10),
              }))
            }
          />
        </div>
      </div>

      <div>
        <div className="as-field__label" style={{ marginBottom: 4 }}>
          격상 대상 severity
        </div>
        <div className="as-chip-row" role="radiogroup">
          {ESCALATE_OPTIONS.map((v) => {
            const active = draft.escalateTo === v;
            return (
              <label
                key={v}
                className={`as-chip ${active ? 'as-chip--active' : ''}`}
              >
                <input
                  type="radio"
                  name="as-warn-escalate"
                  value={v}
                  checked={active}
                  disabled={!editing || isSaving}
                  onChange={() =>
                    setDraft((p) => ({ ...p, escalateTo: v }))
                  }
                />
                <span>{v}</span>
              </label>
            );
          })}
        </div>
      </div>

      {!valid && editing && (
        <div className="as-warn" role="alert">
          시간 창과 임계 빈도는 모두 1 이상의 정수여야 합니다.
        </div>
      )}
    </section>
  );
}
